/**
 * Returns Dual-Write Test
 *
 * Tests that return operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * R1: Create return (money back with stock update)
 * R2: Create return (exchange without stock update)
 * R3: Delete return
 */

import {
  saveNewSale,
  deleteSale,
  saveSaleReturn,
  deleteSaleReturn,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, productsDictAtom, dbAtom, constantsAtom } from '../global/AtomsProvider';

// Test variation definitions
export const RETURN_TEST_VARIATIONS = [
  { id: 'R1', category: 'create', name: 'Create return (money back with stock update)' },
  { id: 'R2', category: 'create', name: 'Create return (exchange without stock update)' },
  { id: 'R3', category: 'delete', name: 'Delete return' },
];

// Get test categories for UI grouping
export function getReturnTestCategories() {
  return [
    { id: 'create', name: 'Create Returns', variations: ['R1', 'R2'] },
    { id: 'delete', name: 'Delete Return', variations: ['R3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getReturnTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const constants = jotaiStore.get(constantsAtom);
  const db = jotaiStore.get(dbAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first available store
  const storeEntries = Object.entries(storesDict || {});
  const firstStore = storeEntries[0]?.[1];
  const storeId = firstStore?.id || storeEntries[0]?.[0];

  // Get payment methods from constants (stored as { paymentMethods: { data: [...] } })
  const paymentMethods = constants?.paymentMethods?.data || [];
  const defaultPaymentMethod = paymentMethods.find(p => p.id === 'cash' || p.name === 'Cash' || p.isCash) || paymentMethods[0];

  // Get first few products for cart items
  const productEntries = Object.entries(productsDict || {});
  const testProducts = productEntries.slice(0, 3).map(([id, product]) => ({
    id,
    ...product,
    ref: db.collection('products').doc(id),
  }));

  // Get a wallet ID for the store
  const walletId = storeId ? `${storeId}-cash` : null;

  return {
    organization,
    storeId,
    storeName: firstStore?.name || 'Unknown Store',
    walletId,
    db,
    organizationId: organization.id,
    testProducts,
    paymentMethods,
    defaultPaymentMethod,
  };
}

/**
 * Create a test sale for return testing
 */
async function createTestSale(db, testData, log) {
  const { organizationId, storeId, testProducts, defaultPaymentMethod } = testData;
  const timestamp = Date.now();

  // Build cart items from available products
  const cartItems = testProducts.slice(0, 2).map((product, index) => ({
    id: product.id,
    name: product.name || `Test Product ${index + 1}`,
    quantity: 2,
    price: product.salePrices?.[storeId] || product.price || 100 + index * 50,
    purchasePrice: product.purchasePrice || 50 + index * 25,
    ref: product.ref,
  }));

  const totalPrice = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalPurchasePrice = cartItems.reduce((sum, item) => sum + (item.purchasePrice || 0) * item.quantity, 0);

  const saleData = {
    organizationId,
    store: storeId,
    saleDate: new Date(),
    dailyStatID: `test-return-${timestamp}-${storeId}`,
    cashierUID: 'test-cashier',
    totalCartPrice: totalPrice,
    finalTotalCartPrice: totalPrice,
    totalPurchasePrice,
    cartItems,
    paymentMethod: defaultPaymentMethod || { id: 'cash', name: 'Cash' },
    purchaseMethod: { id: 'shop-offline', name: 'Shop', firestoreId: 'shop-offline' },
    saleType: 'product',
    comment: `Test sale for return ${timestamp}`,
  };

  log(`Creating test sale with total: ${totalPrice}`);
  const saleRef = await saveNewSale(db, saleData);

  return {
    saleRef,
    saleData,
    cartItems,
    totalPrice,
  };
}

/**
 * Compare return via backend API
 */
async function compareReturnViaApi(returnId, returnData, log) {
  try {
    const response = await api.get(`/sales/returns/compare/${returnId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        returnId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'return',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'totalReturnedAmount', sb: 'total_refund' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(returnData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    return {
      returnId,
      foundInSupabase: true,
      matches: differences.length === 0,
      differences,
      warnings: [],
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      returnId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if return was deleted from Supabase
 */
async function checkReturnDeleted(returnId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/sales/returns/compare/${returnId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single return test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, storeId, defaultPaymentMethod } = testData;

  let createdSale = null;
  let createdReturnRef = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'R1': {
        // Create return (money back with stock update)
        log('Creating test sale for return...');
        createdSale = await createTestSale(db, testData, log);

        log('Waiting 5000ms for sale sync...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        // Prepare return data - return first item
        const returnItem = createdSale.cartItems[0];
        const returnAmount = returnItem.price * 1; // Return 1 unit

        const returnInfo = {
          returnType: 1, // Money back
          updateStock: true,
          totalReturnedAmount: returnAmount,
          date: new Date(),
          comment: `Test return ${Date.now()}`,
          returnMethod: defaultPaymentMethod || { id: 'cash', name: 'Cash' },
          dailyStatID: createdSale.saleData.dailyStatID,
        };

        // Mark items as returned
        const updatedCartItems = createdSale.cartItems.map((item, index) => {
          if (index === 0) {
            return {
              ...item,
              isReturned: true,
              returnedQuantity: 1,
              returnedAmount: returnAmount,
            };
          }
          return item;
        });

        const saleWithReturn = {
          ...createdSale.saleData,
          cartItems: updatedCartItems,
          returnInfo,
        };

        log(`Creating return with amount: ${returnAmount}`);
        await saveSaleReturn(
          db,
          createdSale.saleRef,
          saleWithReturn,
          createdSale.saleRef.id,
          'test-cashier',
          null,
          null
        );

        // Get the return ref (newly created in saveSaleReturn)
        log('Waiting 6000ms for return Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Get the return ID from the sale document
        const saleDoc = await createdSale.saleRef.get();
        const saleDataAfterReturn = saleDoc.data();
        const returnId = saleDataAfterReturn?.returnId;

        if (!returnId) {
          result.error = 'Return ID not found on sale document';
          break;
        }

        log(`Return created with ID: ${returnId}`);
        createdReturnRef = db.collection('returns').doc(returnId);

        // Compare
        const comparison = await compareReturnViaApi(returnId, returnInfo, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test return and sale...');
        try {
          await deleteSaleReturn(db, createdReturnRef, createdSale.saleRef, saleDataAfterReturn, returnInfo);
        } catch (e) {
          log(`Return cleanup warning: ${e.message}`);
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        await deleteSale(db, createdSale.saleRef, { ...createdSale.saleData, returnInfo: null }, 'test-cashier');
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'R2': {
        // Create return (exchange without stock update)
        log('Creating test sale for exchange return...');
        createdSale = await createTestSale(db, testData, log);

        log('Waiting 5000ms for sale sync...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        const returnItem = createdSale.cartItems[0];
        const returnAmount = returnItem.price * 1;

        const returnInfo = {
          returnType: 0, // Exchange
          updateStock: false,
          totalReturnedAmount: returnAmount,
          date: new Date(),
          comment: `Test exchange ${Date.now()}`,
          dailyStatID: createdSale.saleData.dailyStatID,
        };

        const updatedCartItems = createdSale.cartItems.map((item, index) => {
          if (index === 0) {
            return {
              ...item,
              isReturned: true,
              returnedQuantity: 1,
              returnedAmount: returnAmount,
            };
          }
          return item;
        });

        const saleWithReturn = {
          ...createdSale.saleData,
          cartItems: updatedCartItems,
          returnInfo,
        };

        log(`Creating exchange return with amount: ${returnAmount}`);
        await saveSaleReturn(
          db,
          createdSale.saleRef,
          saleWithReturn,
          createdSale.saleRef.id,
          'test-cashier',
          null,
          null
        );

        log('Waiting 6000ms for return Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        const saleDoc = await createdSale.saleRef.get();
        const saleDataAfterReturn = saleDoc.data();
        const returnId = saleDataAfterReturn?.returnId;

        if (!returnId) {
          result.error = 'Return ID not found on sale document';
          break;
        }

        log(`Return created with ID: ${returnId}`);
        createdReturnRef = db.collection('returns').doc(returnId);

        const comparison = await compareReturnViaApi(returnId, returnInfo, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test return and sale...');
        try {
          await deleteSaleReturn(db, createdReturnRef, createdSale.saleRef, saleDataAfterReturn, returnInfo);
        } catch (e) {
          log(`Return cleanup warning: ${e.message}`);
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        await deleteSale(db, createdSale.saleRef, { ...createdSale.saleData, returnInfo: null }, 'test-cashier');
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'R3': {
        // Delete return test
        log('Creating test sale for delete return test...');
        createdSale = await createTestSale(db, testData, log);

        log('Waiting 5000ms for sale sync...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        const returnItem = createdSale.cartItems[0];
        const returnAmount = returnItem.price * 1;

        const returnInfo = {
          returnType: 1,
          updateStock: true,
          totalReturnedAmount: returnAmount,
          date: new Date(),
          comment: `Test delete return ${Date.now()}`,
          returnMethod: defaultPaymentMethod || { id: 'cash', name: 'Cash' },
          dailyStatID: createdSale.saleData.dailyStatID,
        };

        const updatedCartItems = createdSale.cartItems.map((item, index) => {
          if (index === 0) {
            return {
              ...item,
              isReturned: true,
              returnedQuantity: 1,
              returnedAmount: returnAmount,
            };
          }
          return item;
        });

        const saleWithReturn = {
          ...createdSale.saleData,
          cartItems: updatedCartItems,
          returnInfo,
        };

        log(`Creating return to delete, amount: ${returnAmount}`);
        await saveSaleReturn(
          db,
          createdSale.saleRef,
          saleWithReturn,
          createdSale.saleRef.id,
          'test-cashier',
          null,
          null
        );

        log('Waiting 6000ms for return Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        const saleDoc = await createdSale.saleRef.get();
        const saleDataAfterReturn = saleDoc.data();
        const returnId = saleDataAfterReturn?.returnId;

        if (!returnId) {
          result.error = 'Return ID not found on sale document';
          break;
        }

        createdReturnRef = db.collection('returns').doc(returnId);

        // Verify it exists first
        const createCheck = await compareReturnViaApi(returnId, returnInfo, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Return was not created in Supabase';
          // Cleanup
          await deleteSale(db, createdSale.saleRef, createdSale.saleData, 'test-cashier');
          break;
        }

        // Now delete
        log('Deleting return...');
        await deleteSaleReturn(db, createdReturnRef, createdSale.saleRef, saleDataAfterReturn, returnInfo);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkReturnDeleted(returnId);
        result.passed = deleted;
        result.comparisonResult = {
          returnId,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        // Cleanup sale
        log('Cleaning up test sale...');
        await deleteSale(db, createdSale.saleRef, { ...createdSale.saleData, returnInfo: null }, 'test-cashier');
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdReturnRef && createdSale) {
      try {
        const saleDoc = await createdSale.saleRef.get();
        const saleData = saleDoc.data();
        if (saleData?.returnId) {
          await deleteSaleReturn(db, createdReturnRef, createdSale.saleRef, saleData, saleData.returnInfo);
        }
      } catch (cleanupError) {
        log(`Return cleanup failed: ${cleanupError.message}`);
      }
    }
    if (createdSale) {
      try {
        await deleteSale(db, createdSale.saleRef, createdSale.saleData, 'test-cashier');
      } catch (cleanupError) {
        log(`Sale cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all return tests
 */
export async function runFullReturnTest(log = console.log, selectedVariations = null) {
  log('Starting return dual-write tests...');

  const testData = getReturnTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Make sure you are logged in.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  if (testData.testProducts.length < 2) {
    log('ERROR: Need at least 2 products to run return tests.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('RETURNS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.storeName} (${testData.storeId})`);
  log(`Test Products: ${testData.testProducts.map(p => p.name || p.id).join(', ')}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || RETURN_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = RETURN_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = RETURN_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  RETURN_TEST_VARIATIONS,
  getReturnTestCategories,
  getReturnTestData,
  runFullReturnTest,
};
